import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'

import { apiZodios } from '~api/apiZodios'
import { ContactBookIcon, DateIcon, TagIcon } from '~assets/icons'
import { Button, InputLabel, TextInput } from '~components/common'
import AddressInputTable from '~components/common/AddressInputTable'
import AttributionInputTable from '~components/common/AttributionInputTable'
import EmailInputTable from '~components/common/EmailInputTable'
import OrganizationSelector from '~components/common/OrganizationSelector'
import PhoneInputTable from '~components/common/PhoneInputTable'
import TagSelector from '~components/common/TagSelector'
import { RootState } from '~redux/rootReducer'
import {
  Address0,
  AttributionCreate,
  ContactEmail0,
  ContactEntryId,
  EmailIdentity,
  IdentityId,
  PhoneNumber0,
  TagData,
  TargetId,
} from '~types'

import useAddContactFormValidation from './AddContactFormValidation'

interface AddContactFormControlProps {
  onSaveSuccess?: (
    contactEntryId: ContactEntryId,
    identityId: IdentityId,
    targetId: TargetId,
  ) => void
  onSaveError?: (error: Error) => void
  onClose?: () => void
  onComplete?: () => void
  initialData?: ContactFormData
  update?: boolean
  inputOrganization?: EmailIdentity | null
  saveCounter?: number
  onFormValidChange?: (isValid: boolean) => void
  hideSubmitButton?: boolean
}

export interface ContactFormData {
  title: string
  firstName: string
  middleName: string
  lastName: string
  suffix: string
  dateOfBirth: Dayjs
  emails: ContactEmail0[]
  phoneNumbers: PhoneNumber0[]
  addresses: Address0[]
  attributions: AttributionCreate[]
  tags: TagData[]
  is_organization: boolean
}

const AddContactFormControl: React.FC<AddContactFormControlProps> = ({
  onSaveSuccess,
  onSaveError,
  onClose,
  onComplete,
  initialData,
  update = false,
  inputOrganization,
  saveCounter = 0,
  onFormValidChange,
  hideSubmitButton = false,
}) => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const defaultOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const [organization, setOrganization] = useState<EmailIdentity | null>(
    inputOrganization || defaultOrganization || null,
  )
  const [title, setTitle] = useState<string>(initialData?.title || '')
  const [firstName, setFirstName] = useState<string>(initialData?.firstName || '')
  const [middleName, setMiddleName] = useState<string>(initialData?.middleName || '')
  const [lastName, setLastName] = useState<string>(initialData?.lastName || '')
  const [suffix, setSuffix] = useState<string>(initialData?.suffix || '')
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(initialData?.dateOfBirth || null)
  const [emails, setEmails] = useState<ContactEmail0[]>(initialData?.emails || [])
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber0[]>(initialData?.phoneNumbers || [])
  const [addresses, setAddresses] = useState<Address0[]>(initialData?.addresses || [])
  const [tags, setTags] = useState<TagData[]>(initialData?.tags || [])
  const [attributions, setAttributions] = useState<AttributionCreate[]>(
    initialData?.attributions || [],
  )
  const [isAddingContact, setIsAddingContact] = useState<boolean>(false)
  const [isEmailsValid, setIsEmailsValid] = useState<boolean>(true)
  const [isPhonesValid, setIsPhonesValid] = useState<boolean>(true)
  const [isAttributesValid, setIsAttributesValid] = useState<boolean>(true)
  const [isOrganization, setIsOrganization] = useState<boolean>(
    initialData?.is_organization || false,
  )
  const [emailError, setEmailError] = useState<string | null>(null)

  const { validationState, isBasicInfoValid, handleBlur, shouldShowError, resetValidation } =
    useAddContactFormValidation(firstName, lastName, isOrganization)

  const isFormValid = !isBasicInfoValid || !isEmailsValid || !isPhonesValid || !isAttributesValid

  const handleSaveContact = async (): Promise<void> => {
    try {
      if (!user) return
      if (!organization) return

      setEmailError(null)
      setIsAddingContact(true)
      const response = await apiZodios.contacts_createContact({
        user_id: user.id,
        organization_identity_id: organization.id,
        is_organization: isOrganization,
        first_name: firstName,
        middle_name: isOrganization ? '' : middleName,
        last_name: isOrganization ? '' : lastName,
        suffix: isOrganization ? '' : suffix,
        date_of_birth: isOrganization ? null : dateOfBirth?.format('YYYY-MM-DD'),
        emails: emails.map((email) => email.address),
        phones: phoneNumbers.map((phone) => phone.phone_number),
        addresses: addresses.map((address) => address.id),
        tags: tags.map((tag) => tag.id),
        attributions: attributions.map((a) => ({
          attribute_id: null,
          name: a.name,
          value: a.value,
        })),
      })
      setIsAddingContact(false)

      if (response.email_address_conflict) {
        setEmailError('A contact with this email address already exists.')
        return
      }

      onSaveSuccess?.(
        response.contact_entry_id as ContactEntryId,
        response.identity_id as IdentityId,
        response.target_id as TargetId,
      )
      onComplete?.()
      handleClose()
    } catch (error) {
      onSaveError?.(error as Error)
    }
  }

  const handleClose = (): void => {
    setTitle('')
    setFirstName('')
    setMiddleName('')
    setLastName('')
    setSuffix('')
    setDateOfBirth(null)
    setEmails([])
    setPhoneNumbers([])
    setAddresses([])
    setTags([])
    setAttributions([])
    setEmailError(null)
    resetValidation()
    onClose?.()
  }

  useEffect(() => {
    onFormValidChange?.(isFormValid)
  }, [isFormValid])

  useEffect(() => {
    if (saveCounter > 0) {
      handleSaveContact()
    }
  }, [saveCounter])

  return (
    <Box component='form'>
      {isAddingContact && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }}>Adding contact...</Typography>
        </Box>
      )}
      <FormControl sx={{ mt: 1, width: '500px', gap: 2 }}>
        <Box className='flex-v-align' sx={{ gap: 1 }}>
          <ContactBookIcon width={24} />
          <h3>Contact Book of</h3>
          {!inputOrganization ? (
            <OrganizationSelector onSelect={setOrganization} preSelectOrg={organization?.id} />
          ) : (
            <h3>{organization?.name}</h3>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={isOrganization}
            onChange={(e) => setIsOrganization(e.target.value === 'true')}
          >
            <FormControlLabel value={false} control={<Radio />} label='Individual' />
            <FormControlLabel value={true} control={<Radio />} label='Organization' />
          </RadioGroup>
        </Box>

        {/* Basic contact information fields */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            '& > :not(:last-child)': { mb: 2 },
          }}
        >
          {!isOrganization && (
            <Box sx={{ mb: 1 }}>
              <InputLabel label='Title' isOptional />
              <TextInput
                placeholder='Mrs'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Box>
          )}
          <Box sx={{ mb: 1 }}>
            <InputLabel label={isOrganization ? 'Name' : 'First Name'} isOptional={false} />
            <TextInput
              placeholder={isOrganization ? 'Organization Name' : 'Abigail'}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => handleBlur('firstName')}
              error={shouldShowError('firstName')}
              helperText={
                shouldShowError('firstName') ? validationState.firstName.error : undefined
              }
            />
          </Box>
          {!isOrganization && (
            <>
              <Box sx={{ mb: 1 }}>
                <InputLabel label='Middle Name' isOptional />
                <TextInput
                  placeholder='Marie'
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                />
              </Box>
              <Box sx={{ mb: 1 }}>
                <InputLabel label='Last Name' isOptional={false} />
                <TextInput
                  placeholder='Adams'
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => handleBlur('lastName')}
                  error={shouldShowError('lastName')}
                  helperText={
                    shouldShowError('lastName') ? validationState.lastName.error : undefined
                  }
                />
              </Box>
              <Box sx={{ mb: 1 }}>
                <InputLabel label='Suffix' isOptional />
                <TextInput
                  placeholder='Jr.'
                  value={suffix}
                  onChange={(e) => setSuffix(e.target.value)}
                />
              </Box>
            </>
          )}
        </Box>

        {!isOrganization && (
          <Box sx={{ width: '100%' }}>
            <InputLabel label='Date of Birth' isOptional icon={DateIcon} />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker<Dayjs>
                value={dateOfBirth}
                onChange={(newValue) => setDateOfBirth(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* Contact information tables */}
        <EmailInputTable
          setEmails={(newEmails) => {
            setEmails(newEmails)
            setEmailError(null)
          }}
          onValidationChange={setIsEmailsValid}
        />
        <PhoneInputTable setPhoneNumbers={setPhoneNumbers} onValidationChange={setIsPhonesValid} />
        <AddressInputTable addresses={addresses} setAddresses={setAddresses} />
        <AttributionInputTable
          setAttributions={setAttributions}
          onValidationChange={setIsAttributesValid}
        />

        {/* Tags section */}
        <Box sx={{ width: '100%' }}>
          <InputLabel label='Tags' isOptional icon={TagIcon} />
          <Box sx={{ width: '100%' }}>
            <TagSelector
              selectedTags={tags}
              onChange={(selectedTags) => setTags(selectedTags)}
              style={{ width: '100%' }} // Make TagSelector full width
            />
          </Box>
        </Box>
      </FormControl>

      {!hideSubmitButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, mb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleSaveContact}
            disabled={isFormValid}
          >
            {update ? 'Update Contact' : 'Save Contact'}
          </Button>
        </Box>
      )}

      <Snackbar
        open={!!emailError}
        autoHideDuration={6000}
        onClose={() => setEmailError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setEmailError(null)} severity='error' sx={{ width: '100%' }}>
          {emailError}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AddContactFormControl
